import axios from 'axios';
import { firmApi, FCAOrganisationDetails, ExistingFirmResponse } from 'api/firm';
import { networkApi, Network } from 'api/network';
import { mortgageClubApi, MortgageClub } from 'api/mortgage-club';
import { FcaResultKind } from './fcaResultKind';

export type FirmFcaResult =
  | { kind: FcaResultKind.Register; data: FCAOrganisationDetails }
  | { kind: FcaResultKind.Existing; firm: ExistingFirmResponse }
  | { kind: FcaResultKind.NotFound };

export type NetworkFcaResult =
  | { kind: FcaResultKind.Register; data: FCAOrganisationDetails }
  | { kind: FcaResultKind.Existing; network: Network }
  | { kind: FcaResultKind.NotFound };

export type ClubFcaResult =
  | { kind: FcaResultKind.Register; data: FCAOrganisationDetails }
  | { kind: FcaResultKind.Existing; club: MortgageClub }
  | { kind: FcaResultKind.NotFound };

const trimFca = (fca: string) => fca.trim();

function getAxiosStatus(e: unknown): number | undefined {
  if (axios.isAxiosError(e)) {
    return e.response?.status;
  }
  if (e instanceof Error && axios.isAxiosError(e.cause)) {
    return e.cause.response?.status;
  }
  return undefined;
}

async function runFcaLookup<TResult extends FirmFcaResult | NetworkFcaResult | ClubFcaResult, TEntity>(
  fca: string,
  params: {
    getByFca: (trimmed: string) => Promise<TEntity | null | undefined>;
    getFromRegister: (trimmed: string) => Promise<FCAOrganisationDetails>;
    toExisting: (entity: TEntity) => Extract<TResult, { kind: FcaResultKind.Existing }>;
  },
): Promise<TResult> {
  const n = trimFca(fca);
  const found = await (async () => {
    try {
      return await params.getByFca(n);
    } catch (e) {
      if (getAxiosStatus(e) === 404) {
        return null;
      }
      throw e;
    }
  })();
  if (found != null) {
    return params.toExisting(found) as TResult;
  }
  try {
    const data = await params.getFromRegister(n);
    return { kind: FcaResultKind.Register, data } as TResult;
  } catch (e) {
    if (getAxiosStatus(e) === 404) {
      return { kind: FcaResultKind.NotFound } as TResult;
    }
    throw e;
  }
}

export async function runFirmFcaLookup(fca: string): Promise<FirmFcaResult> {
  return runFcaLookup<FirmFcaResult, ExistingFirmResponse>(fca, {
    getByFca: trimmed => firmApi.getFirmByFca(trimmed),
    getFromRegister: trimmed => firmApi.getFirmDetails(trimmed),
    toExisting: firm => ({ kind: FcaResultKind.Existing, firm }),
  });
}

export async function runNetworkFcaLookup(fca: string): Promise<NetworkFcaResult> {
  return runFcaLookup<NetworkFcaResult, Network>(fca, {
    getByFca: trimmed => networkApi.getNetworkByFca(trimmed),
    getFromRegister: trimmed => networkApi.getNetworkFromFcaRegister(trimmed),
    toExisting: network => ({ kind: FcaResultKind.Existing, network }),
  });
}

export async function runClubFcaLookup(fca: string): Promise<ClubFcaResult> {
  return runFcaLookup<ClubFcaResult, MortgageClub>(fca, {
    getByFca: trimmed => mortgageClubApi.getMortgageClubByFca(trimmed),
    getFromRegister: trimmed => mortgageClubApi.getMortgageClubFromFcaRegister(trimmed),
    toExisting: club => ({ kind: FcaResultKind.Existing, club }),
  });
}
